import { useEffect, useState } from 'react';
import {
  Check,
  Loader2,
  Search,
  FileText,
  BarChart3,
  List,
  Sparkles,
  GitCompare,
  Tags,
  Calculator,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import useTranslation from '@/hooks/useTranslation';
import type { ToolStep } from './useResearchChat';

const TOOL_ICONS: Record<string, typeof Search> = {
  _thinking: Sparkles,
  get_project_design: FileText,
  get_overall_analysis: BarChart3,
  get_question_analysis: FileText,
  search_transcripts: Search,
  get_filter_list: List,
  generate_artifact: Sparkles,
  get_comparison: GitCompare,
  get_categories: Tags,
  compute_statistics: Calculator,
};

const TOOL_LABEL_KEYS: Record<string, string> = {
  _thinking: 'msg.preparing',
  get_project_design: 'msg.readingProjectDesign',
  get_overall_analysis: 'msg.checkingOverallAnalysis',
  get_question_analysis: 'msg.checkingQuestionAnalysis',
  search_transcripts: 'msg.searchingTranscripts',
  get_filter_list: 'msg.loadingFilters',
  generate_artifact: 'msg.generatingOutput',
  get_comparison: 'msg.loadingComparison',
  get_categories: 'msg.loadingCategories',
  compute_statistics: 'msg.computingStatistics',
};

interface ToolProgressStepperProps {
  steps: ToolStep[];
  collapsed?: boolean;
}

/**
 * Formats an elapsed-time span for the running-tool indicator.
 *   < 60s →  "12s"
 *   60s-59m → "1m 32s"
 *   ≥ 1h   → "1h 4m"
 */
function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function ToolProgressStepper({ steps, collapsed }: ToolProgressStepperProps) {
  const { lang } = useTranslation();
  // Tick once per second so the elapsed-time counter updates while a
  // tool is running. We only set up the interval when there's at least
  // one running step — idle steppers don't re-render every second.
  const hasRunning = steps?.some((s) => s.status === 'running') ?? false;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!hasRunning) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [hasRunning]);

  if (!steps || steps.length === 0) return null;

  if (collapsed) {
    const doneCount = steps.filter((s) => s.status === 'done').length;
    // Compute total duration from first tool start to last tool completion
    const startTimes = steps.filter((s) => s.startedAt).map((s) => s.startedAt!);
    const minStart = startTimes.length > 0 ? Math.min(...startTimes) : 0;
    const totalDuration = minStart > 0 ? formatElapsed(Date.now() - minStart) : '';
    return (
      <span className="text-[11px] text-neutral-400">
        {doneCount} {doneCount !== 1 ? lang.get('msg.raToolsUsed') : lang.get('msg.raToolUsed')}
        {totalDuration && ` (${totalDuration})`}
      </span>
    );
  }

  const now = Date.now();

  return (
    // role="status" + aria-live="polite" announces tool progress to
    // screen readers without interrupting them. Important because tool
    // execution can take 10+ seconds (and generate_artifact can take
    // up to ~2 minutes) before any further text appears.
    <div className="flex flex-col gap-1.5 py-2" role="status" aria-live="polite">
      {steps.map((step) => {
        const Icon = TOOL_ICONS[step.tool_name] || Search;
        const labelKey = TOOL_LABEL_KEYS[step.tool_name];
        const label = labelKey ? lang.get(labelKey) : step.tool_name;
        const elapsed =
          step.startedAt && step.status === 'running'
            ? formatElapsed(now - step.startedAt)
            : null;
        const isLongRunning =
          step.startedAt && step.status === 'running' && now - step.startedAt > 10_000;

        return (
          <div
            key={step.tool_call_id}
            className={cn(
              'flex items-center gap-2 text-xs rounded-md transition-colors',
              // Running: emerald bg + border gives the step visual
              // weight so users can see "something is happening" even
              // when the rest of the message bubble is static. Done
              // steps are subtle neutral; errors are red.
              step.status === 'running' &&
                'bg-emerald-50/70 border border-emerald-200 px-2 py-1.5 text-emerald-700 font-medium',
              step.status === 'done' && 'text-neutral-400 px-2 py-0.5',
              step.status === 'error' &&
                'bg-red-50 border border-red-200 px-2 py-1.5 text-red-600 font-medium',
            )}
          >
            {step.status === 'running' && (
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            )}
            {step.status === 'done' && <Check className="h-3 w-3 shrink-0" />}
            {step.status === 'error' && (
              <span className="h-3 w-3 text-center shrink-0">!</span>
            )}
            <Icon className="h-3 w-3 shrink-0" />
            <span className="truncate">{label}</span>
            {elapsed && (
              <span
                className={cn(
                  'ml-auto shrink-0 tabular-nums',
                  isLongRunning ? 'text-emerald-600 font-semibold' : 'text-emerald-500',
                )}
              >
                {elapsed}
              </span>
            )}
          </div>
        );
      })}
      {hasRunning && (
        <p className="text-[10px] text-neutral-400 italic px-2 mt-0.5">
          {lang.get('msg.raLargeAnalysisHint')}
        </p>
      )}
    </div>
  );
}

export default ToolProgressStepper;
